import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { Form, Row, Col } from "react-bootstrap";

import { hexToRGBA } from "../../../../utils/theme";

import ProductDropdown from "../../ProductDropdown";
import PSCInfoBlock from "../input-blocks/PSCInfoBlock";
import PrincipalOfficerInfoBlock from "../input-blocks/PrincipalOfficerInfoBlock";
import ActivityBlock from "../input-blocks/ActivityBlock";
import GeneralQuestionsBlock from "../input-blocks/GeneralQuestionsBlock";
import BusinessAddressBlock from "../input-blocks/BusinessAddressBlock";
import DatesBlock from "../input-blocks/DatesBlock";
import CommunicationBlock from "../input-blocks/CommunicationBlock";
import AgreementBlock from "../input-blocks/AgreementBlock";
import Button from "../../styled/Button";

const StyledEINPSCForm = styled(Form)`
  min-height: calc(100vh - 100px);
  
  .inputs-block-title {
    padding: 16px;
    border-bottom: 1px solid ${hexToRGBA('#000000', 0.1)};
    background: ${hexToRGBA('#000000', 0.03)};
    font-family: 'InterMedium';
    font-size: 20px;
    color: #424242;
  }

  .input-title {
    margin-bottom: 4px;
    font-family: 'InterMedium';
    font-size: 14px;
    line-height: calc(1.42857rem * (14 / 16));
    color: ${hexToRGBA('#424242', 0.9)};
    caret-color: ${hexToRGBA('#424242', 0.9)};
  }

  .input-label {
    margin-bottom: 10px;
    font-family: 'InterRegular';
    font-size: 14px;
    line-height: calc(1.42857rem * (14 / 16));
    color: #424242;
  }

  .inputs-block-divider {
    margin-bottom: 16px;
    border-bottom: 1px solid #234e76;
  }

  .disclaimer {
    font-family: 'InterRegular';
    font-size: 10px;
    line-height: 22px;
    text-align: center;
    color: #1B3858;
    opacity: 0.5;
    text-shadow: 1px 1px 0px #FFFFFF;
  }

  button {
    width: 100%;
    margin-bottom: 1rem;
  }

  @media ${({ theme }) => theme.devices.tablet} {
    padding-bottom: 42px;
  }
`;

function EINPSCForm({
  mobile,
  values = {},
  onChange,
  onSubmit,
  onBack,
  onMouseEnter,
  onMouseLeave,
}) {
  const handleSubmitButtonMouseDown = useCallback(() => {
    onChange && onChange({ target: { id: "suspendedSubmit", value: true } });
  }, [onChange]);

  const mobileBlocks = useMemo(() => {
    switch (values?.step) {
      case 1:
        return (
          <>
            <PSCInfoBlock
              values={values}
              onChange={onChange}
            />
            <Row>
              <Col xs={0} md={3} />
              <Col xs={12} md={6}>
                <Button
                  type="submit"
                  id="EINSubmitButton"
                  onMouseDown={handleSubmitButtonMouseDown}
                >
                  Continue
                </Button>
              </Col>
              <Col xs={0} md={3} />
            </Row>
          </>
        );
      case 2:
        return (
          <>
            <PrincipalOfficerInfoBlock
              values={values}
              onChange={onChange}
            />
            <Row>
              <Col xs={4}>
                <Button onClick={onBack}>
                  Back
                </Button>
              </Col>
              <Col xs={8}>
                <Button
                  type="submit"
                  id="EINSubmitButton"
                  onMouseDown={handleSubmitButtonMouseDown}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </>
        );
      case 3:
        return (
          <>
            <ActivityBlock
              values={values}
              onChange={onChange}
            />
            <Row>
              <Col xs={4}>
                <Button onClick={onBack}>
                  Back
                </Button>
              </Col>
              <Col xs={8}>
                <Button
                  type="submit"
                  id="EINSubmitButton"
                  onMouseDown={handleSubmitButtonMouseDown}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </>
        );
      case 4:
        return (
          <>
            <GeneralQuestionsBlock
              values={values}
              onChange={onChange}
            />
            <Row>
              <Col xs={4}>
                <Button onClick={onBack}>
                  Back
                </Button>
              </Col>
              <Col xs={8}>
                <Button
                  type="submit"
                  id="EINSubmitButton"
                  onMouseDown={handleSubmitButtonMouseDown}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </>
        );
      case 5:
        return (
          <>
            <BusinessAddressBlock
              values={values}
              onChange={onChange}
            />
            <Row>
              <Col xs={4}>
                <Button onClick={onBack}>
                  Back
                </Button>
              </Col>
              <Col xs={8}>
                <Button
                  type="submit"
                  id="EINSubmitButton"
                  onMouseDown={handleSubmitButtonMouseDown}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </>
        );
      case 6:
        return (
          <>
            <DatesBlock
              values={values}
              onChange={onChange}
            />
            <Row>
              <Col xs={4}>
                <Button onClick={onBack}>
                  Back
                </Button>
              </Col>
              <Col xs={8}>
                <Button
                  type="submit"
                  id="EINSubmitButton"
                  onMouseDown={handleSubmitButtonMouseDown}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </>
        );
      case 7:
        return (
          <>
            <CommunicationBlock
              values={values}
              onChange={onChange}
            />
            <AgreementBlock />
            <Row>
              <Col xs={4}>
                <Button onClick={onBack}>
                  Back
                </Button>
              </Col>
              <Col xs={8}>
                <Button
                  type="submit"
                  id="EINSubmitButton"
                  onMouseDown={handleSubmitButtonMouseDown}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </>
        );
      default:
        return null;
    }
  }, [values, onChange, onBack, handleSubmitButtonMouseDown]);

  return (
    <StyledEINPSCForm
      id="EINForm"
      noValidate
      validated={values.validated}
      onSubmit={onSubmit}
    >
      <Row>
        <Col xs={12}>
          <ProductDropdown />
        </Col>
      </Row>

      {mobile && mobileBlocks}

      {!mobile && (
        <>
          <PSCInfoBlock
            values={values}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
          <PrincipalOfficerInfoBlock
            values={values}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
          <ActivityBlock
            values={values}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
          <GeneralQuestionsBlock
            values={values}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
          <BusinessAddressBlock
            values={values}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
          <DatesBlock
            values={values}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
          <CommunicationBlock
            values={values}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
          <AgreementBlock />
          <Row>
            <Col xs={0} md={3} />
            <Col xs={12} md={6}>
              <Button
                type="submit"
                id="EINSubmitButton"
                onMouseDown={handleSubmitButtonMouseDown}
              >
                Submit
              </Button>
            </Col>
            <Col xs={0} md={3} />
          </Row>
        </>
      )}
    </StyledEINPSCForm>
  );
}

export default EINPSCForm;
